import { format, formatDistanceToNow } from "date-fns";
import { differenceInMilliseconds, intervalToDuration } from "date-fns";
import { DATE_FORMAT, DATE_STAMP_FORMAT, DATE_TIME_FORMAT, DAY_STAMP_FORMAT } from "@/config";

/** dd/MM/yy */
export const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return format(dateObj, DATE_FORMAT);
};

/** HH:mm:ss dd/MM/yy */
export const formatDateTime = (date: Date | string): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return format(dateObj, DATE_TIME_FORMAT);
};

/**
 * "Aug 2, 2026 · 18:05" — the list-column stamp.
 *
 * Absolute rather than relative on purpose: "about 16 hours ago" answers "how long" but not
 * "when", so two runs from one evening read as a blur and the column cannot be scanned for a
 * day. Relative time earns its place as the hover title instead.
 */
export const formatDateStamp = (date: Date | string): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return "N/A";
  return format(dateObj, DATE_STAMP_FORMAT);
};

/**
 * "Aug 2, 2026" — for columns where the clock adds nothing: an artifact is uploaded once, so
 * the minute it landed is noise next to the day.
 */
export const formatDayStamp = (date: Date | string): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return "N/A";
  return format(dateObj, DAY_STAMP_FORMAT);
};

/** HH:mm — the time under a day stamp, where seconds are noise. */
export const formatClock = (date: Date | string): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return format(dateObj, "HH:mm");
};

/** HH:mm:ss — for log lines. */
export const formatTime = (date: Date | string): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return format(dateObj, "HH:mm:ss");
};

/** MM:SS.ms, e.g. 01:23.45 — for media timestamps. */
export const formatDurationAsTimestamp = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const ms = Math.floor((milliseconds % 1000) / 10);
  const duration = {
    minutes: Math.floor(totalSeconds / 60),
    seconds: totalSeconds % 60,
  };
  return `${String(duration.minutes).padStart(2, "0")}:${String(duration.seconds).padStart(2, "0")}.${String(ms).padStart(2, "0")}`;
};

const pad = (value: number, length = 2) => String(value).padStart(length, "0");

/** A date range as H:MM:SS or MM:SS. */
export const formatInterval = (start: Date, end: Date): string => {
  if (!start) return "N/A";
  if (!end) return "N/A";
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return "N/A";
  const diffMs = differenceInMilliseconds(endDate, startDate);
  if (diffMs < 0) return "N/A";
  const duration = intervalToDuration({ start: 0, end: diffMs });
  if (duration.hours && duration.hours > 0)
    return `${duration.hours}:${pad(duration.minutes || 0)}:${pad(duration.seconds || 0)}`;
  return `${pad(duration.minutes || 0)}:${pad(duration.seconds || 0)}`;
};

/** Milliseconds as H:MM:SS or MM:SS. */
/** A short span as a request timing reads it: "148 ms", "1.24 s", and "<1 ms" for the sub-millisecond phases. */
export const formatMilliseconds = (ms: number): string => {
  if (ms < 1) return "<1 ms";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

export const formatIntervalFromMs = (ms: number): string => {
  const duration = intervalToDuration({ start: 0, end: ms });
  // Roll days into hours so long durations don't silently drop a day.
  const hours = (duration.days || 0) * 24 + (duration.hours || 0);
  // Always zero-pad minutes/seconds — otherwise round values render as "02:" (missing "00").
  if (hours > 0) return `${hours}:${pad(duration.minutes || 0)}:${pad(duration.seconds || 0)}`;
  return `${pad(duration.minutes || 0)}:${pad(duration.seconds || 0)}`;
};

/**
 * A schedule's repeat interval ("Every 24h", "Every 3d"), or null for a one-off. The backend
 * expresses recurrence as a single minute count — no cron, no named cadence — so 0 means the
 * schedule fires once. `minutes` is `next_invokation_in_minutes` (the backend's spelling).
 */
export const formatRepeatInterval = (minutes: number): string | null => {
  if (!minutes || minutes < 0) return null;
  const MINUTES_PER_DAY = 60 * 24;
  // A daily schedule reads as "Every 24h", not "Every 1d".
  if (minutes % MINUTES_PER_DAY === 0 && minutes > MINUTES_PER_DAY) return `Every ${minutes / MINUTES_PER_DAY}d`;
  if (minutes % 60 === 0) return `Every ${minutes / 60}h`;
  return `Every ${minutes}m`;
};

/** Relative time, e.g. "5 minutes ago". */
export const humanInterval = (date: Date | string, options?: { addSuffix?: boolean }): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return "N/A";
  return formatDistanceToNow(dateObj, { addSuffix: options?.addSuffix ?? true });
};

/**
 * A duration at dashboard resolution: "45s", "12m", "3.4h", "49h". Deliberately lossy — these
 * render in KPI tiles and axis labels, where "13:24:07" is too wide and more precision than
 * the number deserves. Fractional hours only below 10h, where the tenth still carries.
 */
export const formatSeconds = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const hours = seconds / 3600;
  return hours < 10 ? `${hours.toFixed(1)}h` : `${Math.round(hours)}h`;
};

/**
 * The same duration without rounding minutes away. For a per-test average `formatSeconds` is
 * too lossy: every bucket on production sits between 117s and 303s, so whole minutes collapse
 * the series onto four labels and render 155s and 174s alike. Drift of half a minute is what
 * this figure is watched for. Only above an hour does it fall back to hours and minutes.
 */
export const formatSecondsPrecise = (seconds: number): string => {
  if (seconds < 3600) return `${Math.round(seconds)}s`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};
